import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { createUseStyles, useTheme } from 'react-jss';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

import { Theme } from '@app/theme';
import { CenteredModal, InformationBox } from 'components';
import {
  CancelParams,
  Participation,
  ParticipationUpdateData,
  useCancelParticipationByClient,
  useIsAdmin,
  useParticipation,
} from '@app/api';
import { Button } from '@app/core';
import { Checkbox, TextArea } from '@app/form';
import { MutationOptions } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  subtitle: {
    ...theme.fonts.body,
    marginTop: theme.marginBase * 2,
  },
  purpleCheckbox: {
    '& label:hover > .checkbox-icon': {
      color: theme.colors.purpleAccessible,
    },
    '& label > .ri-checkbox-line': {
      color: theme.colors.purpleAccessible,
    },
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose?: () => any;
  participation?: Participation | null;
  mutationOptions?: MutationOptions<
    Participation,
    Error,
    CancelParams,
    Participation | ParticipationUpdateData
  >;
}

const validationSchema = Yup.object().shape({
  message: Yup.string().required(),
});

export const CancelingModal = (props: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { data: participation } = useParticipation(props.participation?.id);
  const { appointment } = participation || {};
  const date = appointment && appointment.date;
  const dateIsBefore24Hours = useMemo(() => {
    return date && moment(date).isBefore(moment().add(24, 'hours'));
  }, [date]);
  const [continueIsClicked, setContinueIsClicked] = useState(
    !dateIsBefore24Hours,
  );
  useEffect(() => {
    setContinueIsClicked(!dateIsBefore24Hours);
  }, [props.isOpen, dateIsBefore24Hours]);

  const { mutate } = useCancelParticipationByClient(props.mutationOptions);

  const initialValues = useMemo(
    () => ({
      message: t('sessions.details.cancelingModal.defaultMsg', {
        participant: participation && participation.participant,
        date: appointment && moment(date).format('LLLL'),
      }),
      lastMinuteOverride: false,
    }),
    [t, participation, appointment, date],
  );

  const cancel = async ({
    message,
    lastMinuteOverride,
  }: {
    message: string;
    lastMinuteOverride?: boolean;
  }) => {
    if (!participation) {
      return;
    }
    if (props.onRequestClose) {
      props.onRequestClose();
    }

    mutate({ ...participation, message, lastMinuteOverride });
  };

  return (
    <Formik
      key={participation ? participation.id : 'empty'}
      initialValues={initialValues}
      onSubmit={cancel}
      enableReinitialize={true}
      validationSchema={validationSchema}
    >
      {({ handleSubmit }) => (
        <Form>
          <CenteredModal
            isOpen={props.isOpen}
            onRequestClose={props.onRequestClose}
            title={t('sessions.details.cancelingModal.title')}
            buttons={[
              continueIsClicked && (
                <Button
                  color="ink"
                  variant="transparent"
                  onClick={props.onRequestClose}
                  type="button"
                  key="previous"
                >
                  <Trans i18nKey="sessions.details.cancelingModal.previous" />
                </Button>
              ),
              !continueIsClicked && (
                <Button
                  color="ink"
                  variant="muted"
                  onClick={() => setContinueIsClicked(true)}
                  type="button"
                  key="continue"
                >
                  <Trans i18nKey="sessions.details.cancelingModal.continue" />
                </Button>
              ),
              continueIsClicked && (
                <Button
                  color="red"
                  type="submit"
                  key="submit"
                  onClick={() => handleSubmit()}
                >
                  <Trans i18nKey="sessions.details.cancelingModal.confirm" />
                </Button>
              ),
            ]}
          >
            {dateIsBefore24Hours && (
              <InformationBox
                title={t(
                  'sessions.details.cancelingModal.lastMinuteWarning.title',
                )}
                text={t(
                  'sessions.details.cancelingModal.lastMinuteWarning.message',
                )}
                variant="danger"
              />
            )}
            {continueIsClicked && (
              <>
                <h4 className={classes.subtitle}>
                  <Trans i18nKey="sessions.details.cancelingModal.subtitle" />
                </h4>
                <TextArea name="message" />
                {isAdmin && dateIsBefore24Hours && (
                  <div className={classes.purpleCheckbox}>
                    <Checkbox
                      name="lastMinuteOverride"
                      label={t(
                        'sessions.details.cancelingModal.lastMinuteOverride',
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </CenteredModal>
        </Form>
      )}
    </Formik>
  );
};
